import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarCheck2, Calendar, Users, Link2, Shield, Zap, ArrowRight, Check } from "lucide-react";
import FeatureDemos from "../components/FeatureDemos";
import AppointmentIntelligenceDemos from "../components/AppointmentIntelligenceDemos";
import HomeShowcase from "../components/HomeShowcase";
import { LeadCtaButtons } from "../components/LeadModals";

export default function Landing() {
  return (
    <div className="bg-white dark:bg-zinc-900">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-14 pb-16 md:pt-24 md:pb-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-6 dark:text-zinc-400"
              data-testid="hero-eyebrow"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-blue-600"></span>
              Per palestre · estetica · parrucchieri · studi
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter text-zinc-900 leading-[1.02] dark:text-zinc-100"
            >
              Il tuo calendario.<br />
              <span className="text-zinc-500 dark:text-zinc-400">I tuoi clienti. Un link.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-5 text-zinc-600 text-base md:text-lg max-w-lg dark:text-zinc-400"
            >
              Crea la tua attività, condividi un link e i tuoi clienti prenotano in 3 tap.
              Niente telefonate, niente doppie prenotazioni, niente attrito.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 flex flex-col gap-3"
              data-testid="hero-cta-block"
            >
              <LeadCtaButtons />
              <Link to="/login" className="text-sm text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 inline-flex items-center gap-1" data-testid="cta-login">
                Hai già un account? Accedi
              </Link>
            </motion.div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-zinc-500 dark:text-zinc-400">
              <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600" /> Setup in 2 minuti</span>
              <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600" /> Nessuna carta richiesta</span>
              <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600" /> Link personale da condividere</span>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden card-soft">
              <img
                src="https://images.pexels.com/photos/13068357/pexels-photo-13068357.jpeg"
                alt="dashboard prenotoo"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-5 left-5 right-5 bg-white/90 backdrop-blur-md rounded-2xl p-3 flex items-center gap-3 shadow-[0_20px_40px_rgb(0,0,0,0.10)]">
                <div className="h-10 w-10 rounded-xl bg-zinc-900 text-white flex items-center justify-center">
                  <Link2 className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-widest text-zinc-500 dark:text-zinc-400">Link pubblico</div>
                  <div className="text-sm font-medium truncate">prenotoo.it/book/studio-belle</div>
                </div>
              </div>
              <div className="absolute bottom-5 left-5 right-5 bg-white/90 backdrop-blur-md rounded-2xl p-4 flex items-center gap-3 shadow-[0_20px_40px_rgb(0,0,0,0.12)]">
                <div className="h-11 w-11 rounded-xl bg-blue-600 text-white flex items-center justify-center">
                  <CalendarCheck2 className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">Nuova prenotazione · Manicure</div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">Oggi · 17:30 · Cliente: Anna R.</div>
                </div>
                <div className="text-xs font-semibold text-emerald-600">Confermata</div>
              </div>
            </div>
            <div className="absolute -top-6 -left-6 h-24 w-24 rounded-full bg-blue-600/10 blur-2xl" />
          </motion.div>
        </div>
      </section>

      {/* FEATURE GRID */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-2xl mb-12">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600 mb-3">Tutto quello che ti serve</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium">
            Una dashboard. Zero stress.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {[
            { icon: Calendar, title: "Agenda intelligente", desc: "Vista giornaliera, slot automatici e buffer tra appuntamenti. Mai più sovrapposizioni." },
            { icon: Link2, title: "Link pubblico da condividere", desc: "Un URL personalizzato da mettere su Instagram, WhatsApp o sito. I clienti prenotano da soli." },
            { icon: Users, title: "Gestione servizi e staff", desc: "Definisci durata, prezzo e buffer. Aggiungi operatori multipli con disponibilità indipendente." },
            { icon: Shield, title: "Anti doppia prenotazione", desc: "Il sistema blocca automaticamente gli slot già occupati o in ferie." },
            { icon: Zap, title: "3 tap per prenotare", desc: "Servizio → data → orario. I tuoi clienti impiegano meno di 30 secondi." },
            { icon: CalendarCheck2, title: "Storico completo", desc: "Ogni cliente vede le proprie prenotazioni, passate e future, con annullo istantaneo." },
          ].map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="card-soft p-6"
            >
              <div className="h-11 w-11 rounded-2xl bg-zinc-900 text-white flex items-center justify-center mb-4 dark:bg-zinc-100 dark:text-zinc-900">
                <f.icon className="h-5 w-5" />
              </div>
              <div className="font-display text-xl font-medium">{f.title}</div>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed dark:text-zinc-400">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* UNIQUE FEATURES (visual demos) */}
      <FeatureDemos />

      {/* INTERACTIVE SHOWCASE — 3-tap demo, ROI calculator, before/after */}
      <HomeShowcase />

      {/* APPOINTMENT INTELLIGENCE (waitlist, auto-fill, auto-book, loyalty/combo) */}
      <AppointmentIntelligenceDemos />

      {/* HOW IT WORKS */}
      <section className="bg-zinc-50 border-y border-zinc-100 dark:bg-zinc-800 dark:border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="max-w-2xl mb-12">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600 mb-3">Come funziona</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium">Tre passi. Zero setup tecnico.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Ti attiviamo noi l'account", d: "Contattaci: configuriamo insieme l'account con la tua attività, i servizi e gli orari. Ricevi le credenziali in 24h." },
              { n: "02", t: "Condividi il tuo link", d: "Copia il tuo URL di prenotazione e mettilo ovunque: bio Instagram, WhatsApp, sito." },
              { n: "03", t: "Ricevi prenotazioni", d: "I clienti scelgono servizio e orario. Tu vedi tutto in agenda, in tempo reale." },
            ].map((s) => (
              <div key={s.n} className="bg-white rounded-3xl p-6 border border-zinc-100 dark:bg-zinc-900 dark:border-zinc-800">
                <div className="font-display text-5xl text-zinc-200">{s.n}</div>
                <div className="font-display text-xl font-medium mt-2">{s.t}</div>
                <p className="text-sm text-zinc-600 mt-2 dark:text-zinc-400">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="relative overflow-hidden rounded-[2rem] bg-zinc-900 text-white p-10 md:p-16 grain-bg">
          <div className="relative max-w-2xl">
            <h3 className="font-display text-3xl md:text-5xl tracking-tighter font-medium">
              Pronto a smettere di rispondere al telefono?
            </h3>
            <p className="mt-4 text-zinc-300 max-w-lg">
              Attivazione su invito: prenota una demo o richiedi l'attivazione, ti contattiamo entro 24h.
            </p>
            <div className="mt-8">
              <LeadCtaButtons variant="dark" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
